import React from 'react';
import { Helmet } from 'react-helmet';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  getComparisonSchema,
  getBreadcrumbSchema
} from '../../utils/seoSchemas';

const comparisonData = [
  { feature: 'AI-powered email booking intake', goOps: true, competitor: false },
  { feature: 'Visual drag-and-drop job board', goOps: true, competitor: true },
  { feature: 'Digital vehicle health checks (eVHC)', goOps: true, competitor: true },
  { feature: 'Photo and video uploads', goOps: true, competitor: true },
  { feature: 'Customer payment links', goOps: true, competitor: true },
  { feature: 'Automatic VAT-compliant invoicing', goOps: true, competitor: true },
  { feature: 'UK VAT rules built-in', goOps: true, competitor: true },
  { feature: 'Book My Garage integration', goOps: true, competitor: false },
  { feature: 'Parts ordering integration (GSF, Euro)', goOps: true, competitor: false },
  { feature: 'MOT reminder system', goOps: true, competitor: true },
  { feature: 'SMS notifications', goOps: true, competitor: true },
  { feature: 'Mobile-friendly interface', goOps: true, competitor: true },
];

const GarageHive = () => {
  const comparisonSchema = getComparisonSchema({
    competitor: 'GarageHive',
    description: 'Compare Go Ops vs GarageHive garage management software. See feature differences in AI automation, integrations, and UK-specific capabilities.',
    url: 'https://www.go-ops.digital/compare/garagehive'
  });
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Home', url: 'https://www.go-ops.digital/' },
    { name: 'Compare', url: 'https://www.go-ops.digital/compare' },
    { name: 'Go Ops vs GarageHive', url: 'https://www.go-ops.digital/compare/garagehive' }
  ]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <Helmet>
        <title>Go Ops vs GarageHive | Garage Management Software Comparison</title>
        <meta
          name="description"
          content="Compare Go Ops vs GarageHive garage management software. See feature differences in AI automation, integrations, and UK-specific capabilities."
        />
        <link rel="canonical" href="https://www.go-ops.digital/compare/garagehive" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Go Ops" />
        <meta property="og:title" content="Go Ops vs GarageHive | Garage Management Software Comparison" />
        <meta
          property="og:description"
          content="Compare Go Ops vs GarageHive garage management software. See feature differences in AI automation and integrations."
        />
        <meta property="og:url" content="https://www.go-ops.digital/compare/garagehive" />
        <meta property="og:image" content="https://www.go-ops.digital/goopslogo.jpeg" />
        <meta property="og:locale" content="en_GB" />
        <script type="application/ld+json">
          {JSON.stringify(comparisonSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      {/* Navigation */}
      <Box sx={{ bgcolor: '#2c3e50', py: 2 }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography
              variant="h6"
              component={RouterLink}
              to="/"
              sx={{ color: 'white', textDecoration: 'none', fontWeight: 'bold' }}
            >
              Go Ops
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button variant="text" component={RouterLink} to="/features" sx={{ color: 'white' }}>
                Features
              </Button>
              <Button variant="text" component={RouterLink} to="/pricing" sx={{ color: 'white' }}>
                Pricing
              </Button>
              <Button variant="text" component={RouterLink} to="/contact" sx={{ color: 'white' }}>
                Contact
              </Button>
              <Button
                variant="contained"
                component={RouterLink}
                to="/auth/login"
                sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e65100' } }}
              >
                Login
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box sx={{ bgcolor: '#2c3e50', color: 'white', py: 6, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h1" component="h1" sx={{ fontSize: '2.5rem', fontWeight: 'bold', mb: 2 }}>
            Go Ops vs GarageHive
          </Typography>
          <Typography variant="h2" component="p" sx={{ fontSize: '1.2rem', opacity: 0.9 }}>
            Compare garage management software features
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 4, lineHeight: 1.8 }}>
          Both Go Ops and GarageHive are garage management systems designed for UK workshops. The key
          difference is that Go Ops has AI-powered email intake that automatically extracts booking
          details from emails, while GarageHive requires manual data entry. Go Ops also integrates
          directly with Book My Garage and UK parts suppliers.
        </Typography>

        {/* Comparison Table */}
        <TableContainer component={Paper} sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#2c3e50' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Feature</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Go Ops</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>GarageHive</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comparisonData.map((row, index) => (
                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f8f9fa' } }}>
                  <TableCell>{row.feature}</TableCell>
                  <TableCell align="center">
                    {row.goOps ? (
                      <CheckIcon sx={{ color: '#28a745' }} />
                    ) : (
                      <CloseIcon sx={{ color: '#dc3545' }} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {row.competitor ? (
                      <CheckIcon sx={{ color: '#28a745' }} />
                    ) : (
                      <CloseIcon sx={{ color: '#dc3545' }} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Key Differences */}
        <Paper sx={{ p: 4, mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: 3 }}>
            Key Differences
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>AI Email Intake:</strong> Go Ops automatically reads booking emails from Book My Garage
            and extracts customer details, vehicle information, and requested services. GarageHive requires
            manual copying from emails into the system.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Parts Integration:</strong> Go Ops integrates directly with GSF Car Parts and Euro Car Parts
            for searching and ordering parts within the system. Search by vehicle registration and part type,
            then auto-fill part numbers and prices.
          </Typography>
          <Typography variant="body1">
            <strong>Book My Garage:</strong> Go Ops processes Book My Garage emails automatically, including
            multi-location support for garages with multiple branches.
          </Typography>
        </Paper>

        {/* CTA */}
        <Box sx={{ textAlign: 'center', p: 4, bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
            See Go Ops in action
          </Typography>
          <Typography sx={{ mb: 3, color: '#666' }}>
            Book a demo to see how Go Ops compares to your current system.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              component={RouterLink}
              to="/contact"
              sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e65100' } }}
            >
              Book a Demo
            </Button>
            <Button
              variant="outlined"
              component={RouterLink}
              to="/features"
              sx={{ borderColor: '#2c3e50', color: '#2c3e50' }}
            >
              View All Features
            </Button>
          </Stack>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: '#2c3e50', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Typography variant="body2">
              &copy; {new Date().getFullYear()} Go Ops. All rights reserved.
            </Typography>
            <Stack direction="row" spacing={3}>
              <Button component={RouterLink} to="/features" sx={{ color: 'white', textTransform: 'none' }}>
                Features
              </Button>
              <Button component={RouterLink} to="/pricing" sx={{ color: 'white', textTransform: 'none' }}>
                Pricing
              </Button>
              <Button component={RouterLink} to="/contact" sx={{ color: 'white', textTransform: 'none' }}>
                Contact
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default GarageHive;
